import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  TaskManagementRemindCreateDocument,
  TaskManagementRemindDeleteDocument,
  TaskManagementRemindGetByIdDocument,
  TaskManagementRemindListByRelatedEntityDocument,
  TaskManagementRemindListDocument,
  TaskManagementRemindListPaginatedDocument,
  TaskManagementRemindListRelatedEntitiesDocument,
  TaskManagementRemindUpdateDocument,
} from "api/generated";

import { Optional } from "config/types";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  ITaskManagementRemindModel,
  ITaskManagementRemindCreateVars,
  ITaskManagementRemindPaginatedResponse,
  ITaskManagementRemindPaginatedVars,
  ITaskManagementRemindUpdateVars,
  ITaskManagementRemindRelatedEntity,
  ITaskManagementRemindForModalModel,
  ITaskManagementRemindListModel,
} from "./types";

export async function getTaskManagementRemind(
  id: number
): Promise<Optional<ITaskManagementRemindModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: TaskManagementRemindGetByIdDocument,
      variables: {
        id,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.taskManagementRemindGetById;
  } catch (error) {
    throw error;
  }
}

export async function getTaskManagementRemindListByRelatedEntities(
  entityTypeName: string,
  relatedEntityId: number
): Promise<ITaskManagementRemindForModalModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: TaskManagementRemindListByRelatedEntityDocument,
      variables: {
        entityTypeName,
        relatedEntityId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.taskManagementRemindListByRelatedEntity || [];
  } catch (error) {
    throw error;
  }
}

export async function getTaskManagementRemindPaginated(
  variables: ITaskManagementRemindPaginatedVars
): Promise<Optional<ITaskManagementRemindPaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: TaskManagementRemindListPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.taskManagementRemindListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function getTaskManagementRemindList(
  filters: ITaskManagementRemindPaginatedVars["filters"]
): Promise<ITaskManagementRemindListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: TaskManagementRemindListDocument,
      variables: {
        filters,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.taskManagementRemindList || [];
  } catch (error) {
    throw error;
  }
}

export async function getTaskManagementRemindRelatedEntities(
  entityTypeId: number
): Promise<ITaskManagementRemindRelatedEntity[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: TaskManagementRemindListRelatedEntitiesDocument,
      variables: { entityTypeId },
      fetchPolicy: "cache-first",
    });

    return res?.data?.taskManagementRemindListRelatedEntities ?? [];
  } catch (error) {
    throw error;
  }
}

export async function createTaskManagementRemind(
  variables: ITaskManagementRemindCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: TaskManagementRemindCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "TaskManagementRemind",
      fieldNames: [
        "taskManagementRemindList",
        "taskManagementRemindListPaginated",
        "taskManagementRemindListByRelatedEntity",
      ],
    });

    return Boolean(res?.data?.taskManagementRemindCreate);
  } catch (error) {
    throw error;
  }
}

export async function updateTaskManagementRemind(
  variables: ITaskManagementRemindUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: TaskManagementRemindUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "TaskManagementRemind",
      id: variables.id.toString(),
      fieldNames: [
        "taskManagementRemindList",
        "taskManagementRemindListPaginated",
        "taskManagementRemindListByRelatedEntity",
        "taskManagementRemindGetById",
      ],
    });

    return Boolean(res?.data?.taskManagementRemindUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deleteTaskManagementRemind(
  id: number
): Promise<Optional<boolean>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: TaskManagementRemindDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "TaskManagementRemind",
      id: id.toString(),
      fieldNames: [
        "taskManagementRemindList",
        "taskManagementRemindListPaginated",
        "taskManagementRemindListByRelatedEntity",
      ],
    });

    return res?.data?.taskManagementRemindDelete;
  } catch (error) {
    throw error;
  }
}
